import React, { useEffect, useState } from 'react';
import { Send, Star, ThumbsUp } from 'lucide-react';
import DashModal from '../dashboard/DashModal';
import { StarInput } from '../common/Stars';

const RATING_HINTS = ['', 'Très insatisfait', 'Insatisfait', 'Correct', 'Satisfait', 'Excellent'];

const RECOMMEND_OPTIONS = [
  { v: 'yes', label: 'Oui' },
  { v: 'no', label: 'Non' },
  { v: 'unsure', label: 'Je ne sais pas' },
];

// Formulaire d'évaluation de l'expérience Tarjemlek — une commande livrée = un avis.
export default function ReviewModal({ open, order, existing, busy, error, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [recommend, setRecommend] = useState('');
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setRating(existing?.rating || 0);
    setComment(existing?.comment || '');
    setRecommend(existing?.recommend || '');
    setLocalError('');
  }, [open, existing]);

  const submit = () => {
    if (!rating) return setLocalError('Veuillez attribuer une note globale.');
    if (!recommend) return setLocalError('Merci d’indiquer si vous recommandez Tarjemlek.');
    setLocalError('');
    onSubmit({ rating, comment, recommend });
  };

  return (
    <DashModal
      open={open}
      title={existing ? 'Modifier mon avis' : 'Évaluer votre expérience'}
      subtitle={order ? `${order.id} · ${order.document || ''} · ${order.langs || ''}` : ''}
      onClose={() => !busy && onClose()}
    >
      <div className="dash-form" style={{ maxWidth: '100%' }}>
        {/* Note globale — bloc centré et aéré */}
        <div
          style={{
            textAlign: 'center',
            padding: '18px 16px 14px',
            borderRadius: 14,
            background: 'linear-gradient(180deg, #f8fafc, #fff)',
            border: '1px solid var(--gray-100)',
            marginBottom: 16,
          }}
        >
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: 'var(--navy)', marginBottom: 10 }}>
            Votre note globale *
          </label>
          <StarInput value={rating} onChange={setRating} size={34} />
          <div style={{ marginTop: 8, minHeight: 18, fontSize: '0.8rem', fontWeight: 600, color: rating ? '#b8860b' : 'var(--gray-400)' }}>
            {rating ? `${RATING_HINTS[rating]} · ${rating}/5` : 'Touchez une étoile pour noter'}
          </div>
        </div>

        <div className="dash-field">
          <label>Votre commentaire (facultatif)</label>
          <textarea
            rows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Racontez votre expérience : démarche, délais, qualité du service…"
          />
        </div>

        <div className="dash-field">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <ThumbsUp size={13} /> Recommanderiez-vous Tarjemlek ? *
          </label>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
            {RECOMMEND_OPTIONS.map((o) => {
              const active = recommend === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setRecommend(o.v)}
                  aria-pressed={active}
                  style={{
                    flex: '1 1 110px',
                    padding: '10px 14px',
                    borderRadius: 12,
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `1.5px solid ${active ? 'var(--teal)' : 'var(--gray-200)'}`,
                    background: active ? 'rgba(26,122,122,0.1)' : 'var(--white)',
                    color: active ? 'var(--teal)' : 'var(--gray-600)',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        <p style={{ fontSize: '0.75rem', color: 'var(--gray-400)', margin: '4px 0 0', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
          <Star size={11} style={{ flexShrink: 0, marginTop: 2 }} />
          Votre avis sera associé à cette commande vérifiée. Évitez d’inclure des informations
          personnelles ou le contenu confidentiel de vos documents.
        </p>

        {(localError || error) && (
          <p style={{ color: '#b91c1c', fontSize: '0.85rem', margin: '8px 0 0' }}>{localError || error}</p>
        )}

        <div className="dash-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            <Send size={14} /> {busy ? 'Envoi…' : existing ? 'Enregistrer' : 'Soumettre mon avis'}
          </button>
          <button type="button" className="btn btn-outline" onClick={onClose} disabled={busy}>
            Annuler
          </button>
        </div>
      </div>
    </DashModal>
  );
}
